import { AdminPageHeader } from '@/components/patterns/admin-page'
import { EmptyState } from '@/components/patterns/empty-state'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import SaveBar from '@/components/forms/SaveBar'
import DeliveryRouteBuilderMap from '@/components/orders/DeliveryRouteBuilderMap'
import { getDeliveryRouteMapBucketsForOrders } from '@/components/orders/delivery-route-map-model'
import { compareDeliveryRouteMapListOrders } from '@/components/orders/delivery-route-map-routing'
import {
  DeliveryRouteHiddenSelectionDialog,
  useDeliveryRouteBuilderInteraction,
} from '@/components/orders/DeliveryRouteBuilderSelection'
import ManualDeliveryDialog from '@/components/orders/ManualDeliveryDialog'
import SelectableDeliveryRouteOrderRow from '@/components/orders/SelectableDeliveryRouteOrderRow'
import { isDeliveryAddressExplicitlyUnverified } from '@/components/orders/delivery-address-verification'
import {
  DELIVERY_ROUTE_STOP_LIMIT,
  DELIVERY_ROUTE_STOP_LIMIT_MESSAGE,
} from '@/components/orders/delivery-route-constants'
import {
  applyDeliveryRouteSelectionActions,
  getDeliveryRouteSelectionCount,
} from '@/components/orders/delivery-route-selection'
import {
  buildDeliveryRouteDisplayStops,
  buildDeliveryRouteStopPayload,
  buildManualDeliveryDisplayStop,
  canSaveManualDeliveryDraft,
  getDeliveryRouteEditSessionKey,
  getManualDeliveryDraftSignature,
  getNextManualDeliveryDraftSelectionId,
  mergeManualDeliveryDraftStops,
} from '@/components/orders/delivery-route-stops'
import {
  getDeliveryRouteSelectionId,
  getDeliveryShareErrorMessage,
  isDeliveryRouteEligible,
  isDeliveryRouteStarted,
} from '@/components/orders/delivery-share.utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAppForm } from '@/lib/form'
import { useUpdateDeliveryShareMutation } from '@/lib/services/delivery-shares/delivery-shares.query'
import { salesOrderQueries } from '@/lib/services/sales-orders/sales-orders.query'
import { useStore } from '@tanstack/react-form'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Plus, Route as RouteIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useDeliveryRouteShare } from './-delivery-route-query'

function getRouteEditSalesOrderIds(routeStopOrderIds, lockedStopIdSet, selectedOrderIds) {
  const selectedOrderIdSet = new Set(selectedOrderIds)
  const routeStopIdSet = new Set(routeStopOrderIds)
  const currentRouteOrderIds = routeStopOrderIds.filter(
    (orderId) => lockedStopIdSet.has(orderId) || selectedOrderIdSet.has(orderId)
  )
  const newOrderIds = selectedOrderIds.filter((orderId) => !routeStopIdSet.has(orderId))

  return [...currentRouteOrderIds, ...newOrderIds]
}

function buildRouteEditStopPayload(stopId, availableOrderById, routeStopById) {
  const selectedStop = availableOrderById.get(stopId) ?? routeStopById.get(stopId)

  if (selectedStop) {
    return buildDeliveryRouteStopPayload(selectedStop)
  }

  const salesOrderId = Number(stopId)

  if (!Number.isInteger(salesOrderId) || salesOrderId <= 0) {
    throw new Error(`Unable to resolve delivery route stop ${stopId}.`)
  }

  // Preserve the florist's selected order identity across eligibility refetches.
  // The API revalidates current eligibility under lock when the route is saved.
  return {
    source_type: 'sales_order',
    sales_order_id: salesOrderId,
  }
}

function EditRouteSelectionHeader({
  canAddManualDelivery = false,
  onAddManualDelivery,
  routeMetricSummary,
  selectedOrderCount,
  visibleOrderCount,
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="h5">Available Deliveries</Typography>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!canAddManualDelivery}
              onClick={onAddManualDelivery}
            >
              <Plus className="size-4" />
              Add custom stop
            </Button>
          </div>
          <Typography variant="supporting" className="block">
            {routeMetricSummary || 'Time/Distance: NA'}
          </Typography>
        </div>
        <Typography variant="labelStrong" className="shrink-0 whitespace-nowrap pt-0.5">
          {selectedOrderCount}/{visibleOrderCount} Selected
        </Typography>
      </div>
    </div>
  )
}

export function DeliveryRouteEdit({ routeApi }) {
  const navigate = useNavigate()
  const path = usePathHelper()
  const isRedirectingAfterUpdateRef = useRef(false)
  const { isReadOnly, orgId, share } = useDeliveryRouteShare({ routeApi })
  const { data: deliveryOrders = [] } = useSuspenseQuery({
    ...salesOrderQueries.deliveryRouteOrders({
      organizationId: orgId,
    }),
    refetchInterval: 1000 * 60,
  })
  const updateDeliveryShare = useUpdateDeliveryShareMutation()
  const isStartedRoute = isDeliveryRouteStarted(share)
  const canonicalRouteStops = useMemo(() => buildDeliveryRouteDisplayStops(share), [share])
  const serverManualStops = canonicalRouteStops.filter((stop) => stop.source_type === 'manual')
  const [manualEditStops, setManualEditStops] = useState(serverManualStops)
  const [manualDialogState, setManualDialogState] = useState(null)
  const [manualStopPendingRemoval, setManualStopPendingRemoval] = useState(null)
  const [removedManualStopIds, setRemovedManualStopIds] = useState(() => new Set())
  const hasManualStopDraftChanges =
    getManualDeliveryDraftSignature(manualEditStops) !==
      getManualDeliveryDraftSignature(serverManualStops) || removedManualStopIds.size > 0
  const mergedRouteDisplayStops = useMemo(
    () => mergeManualDeliveryDraftStops(canonicalRouteStops, manualEditStops),
    [canonicalRouteStops, manualEditStops]
  )
  const routeDisplayStops = useMemo(
    () =>
      mergedRouteDisplayStops.filter(
        (stop) => !removedManualStopIds.has(getDeliveryRouteSelectionId(stop))
      ),
    [mergedRouteDisplayStops, removedManualStopIds]
  )
  const lockedStops = routeDisplayStops.filter((stop) => stop.is_delivered)
  const editableStops = routeDisplayStops.filter((stop) => !stop.is_delivered)
  const lockedStopIds = lockedStops.map(getDeliveryRouteSelectionId)
  const routeStopOrderIds = routeDisplayStops.map(getDeliveryRouteSelectionId)
  const initialEditableStopIds = canonicalRouteStops
    .filter((stop) => !stop.is_delivered)
    .map(getDeliveryRouteSelectionId)
  const editableStopIds = editableStops.map(getDeliveryRouteSelectionId)
  const editableStopFormKey = getDeliveryRouteEditSessionKey(share)
  const lockedStopIdSet = new Set(lockedStopIds)
  const editableStopIdSet = new Set(editableStopIds)
  const routeStopIdSet = new Set(routeStopOrderIds)
  const routeStopById = new Map(
    routeDisplayStops.map((stop) => [getDeliveryRouteSelectionId(stop), stop])
  )
  const manualStopIdSet = new Set(
    routeDisplayStops
      .filter((stop) => stop.source_type === 'manual')
      .map(getDeliveryRouteSelectionId)
  )
  const deliveryOrderIdSet = new Set(deliveryOrders.map((order) => order.id))
  const availableOrderCandidates = [
    ...deliveryOrders,
    ...editableStops.filter(
      (order) =>
        order.source_type === 'manual' ||
        !deliveryOrderIdSet.has(getDeliveryRouteSelectionId(order))
    ),
  ]
  const availableOrders = availableOrderCandidates
    .filter(
      (order) =>
        !lockedStopIdSet.has(getDeliveryRouteSelectionId(order)) &&
        (order.source_type === 'manual' ||
          (order.location?.id === share.location?.id &&
            (editableStopIdSet.has(getDeliveryRouteSelectionId(order)) ||
              isDeliveryRouteEligible(order))))
    )
    .map((order) =>
      editableStopIdSet.has(getDeliveryRouteSelectionId(order))
        ? (routeStopById.get(getDeliveryRouteSelectionId(order)) ?? order)
        : order
    )
    .sort((left, right) => {
      const leftIndex = editableStopIds.indexOf(getDeliveryRouteSelectionId(left))
      const rightIndex = editableStopIds.indexOf(getDeliveryRouteSelectionId(right))

      if (leftIndex !== -1 && rightIndex !== -1) {
        return leftIndex - rightIndex
      }

      if (leftIndex !== -1) {
        return -1
      }

      if (rightIndex !== -1) {
        return 1
      }

      return compareDeliveryRouteMapListOrders(left, right)
    })
  const availableOrderById = new Map(
    availableOrders.map((order) => [getDeliveryRouteSelectionId(order), order])
  )
  const availableOrderIds = new Set(availableOrders.map(getDeliveryRouteSelectionId))
  const availableListOrders = [
    ...availableOrders,
    ...routeDisplayStops.filter(
      (order) => !availableOrderIds.has(getDeliveryRouteSelectionId(order))
    ),
  ].sort((left, right) => {
    const leftRouteIndex = routeStopOrderIds.indexOf(getDeliveryRouteSelectionId(left))
    const rightRouteIndex = routeStopOrderIds.indexOf(getDeliveryRouteSelectionId(right))

    if (leftRouteIndex !== -1 && rightRouteIndex !== -1) {
      return leftRouteIndex - rightRouteIndex
    }

    if (leftRouteIndex !== -1) {
      return -1
    }

    if (rightRouteIndex !== -1) {
      return 1
    }

    return compareDeliveryRouteMapListOrders(left, right)
  })
  const editRouteForm = useAppForm({
    defaultValues: {
      name: share.name ?? '',
      sales_order_ids: initialEditableStopIds,
    },
    validators: {
      onChange: ({ value }) => {
        const selectedOrderIdSet = new Set(value.sales_order_ids ?? [])
        const selectedOrders = availableOrders.filter((order) =>
          selectedOrderIdSet.has(getDeliveryRouteSelectionId(order))
        )
        const hasUnverifiedOrder = selectedOrders.some((order) =>
          isDeliveryAddressExplicitlyUnverified(order.delivery_address)
        )

        return hasUnverifiedOrder
          ? 'Fix or remove selected unverified stops before saving this route.'
          : undefined
      },
      onSubmitAsync: async ({ value, formApi }) => {
        const selectedOrderIds = value.sales_order_ids ?? []
        const selectedOrderIdSet = new Set(selectedOrderIds)
        const selectedOrders = availableOrders.filter((order) =>
          selectedOrderIdSet.has(getDeliveryRouteSelectionId(order))
        )
        const hasUnverifiedOrder = selectedOrders.some((order) =>
          isDeliveryAddressExplicitlyUnverified(order.delivery_address)
        )

        if (hasUnverifiedOrder || isReadOnly) {
          return
        }

        try {
          const routeName = String(value.name ?? '').trim() || null
          const stopIds = getRouteEditSalesOrderIds(
            routeStopOrderIds,
            lockedStopIdSet,
            selectedOrderIds
          )
          const shouldPreserveStopOrder = routeStopOrderIds.every((orderId) =>
            stopIds.includes(orderId)
          )
          const stops = stopIds.map((stopId) =>
            buildRouteEditStopPayload(stopId, availableOrderById, routeStopById)
          )

          await updateDeliveryShare.mutateAsync({
            deliveryShareId: share.id,
            ...(share.route_version ? { route_version: share.route_version } : {}),
            name: routeName,
            location_id: share.location?.id,
            ...(shouldPreserveStopOrder ? { preserve_order: true } : {}),
            stops,
          })
          formApi.reset({ ...value, name: routeName ?? '' })
          isRedirectingAfterUpdateRef.current = true
          toast.success('Route updated')
          void navigate({
            ...path.deliveryRoutes.show(share.id).link,
          })
        } catch (error) {
          toast.error('Unable to update route', {
            description: getDeliveryShareErrorMessage(error),
          })
        }
      },
    },
  })
  const selectedEditableOrderIds = useStore(
    editRouteForm.store,
    (state) => state.values.sales_order_ids ?? []
  )
  const selectedMapOrderIds = getRouteEditSalesOrderIds(
    routeStopOrderIds,
    lockedStopIdSet,
    selectedEditableOrderIds
  )
  const selectedRouteStopCount = getDeliveryRouteSelectionCount(
    selectedEditableOrderIds,
    lockedStopIds
  )
  const updateSelectedOrderIds = (selectedOrderIds) => {
    const selectedOrderIdSet = new Set(selectedOrderIds)

    editRouteForm.setFieldValue(
      'sales_order_ids',
      availableOrders
        .filter((order) => selectedOrderIdSet.has(getDeliveryRouteSelectionId(order)))
        .map(getDeliveryRouteSelectionId)
    )
  }
  const {
    confirmPendingMapFilterChange,
    filteredOrders: filteredAvailableListOrders,
    handleCardHoverChange,
    handleMapEnabledBucketsChange,
    highlightedCardOrderIds,
    highlightedMapOrderIdSet,
    mapEnabledBuckets,
    pendingMapFilterChange,
    routeMetricSummary,
    scrollToHighlightedOrderId,
    setHighlightedMapOrderIds,
    setOptimizedRouteSummary,
    setPendingMapFilterChange,
  } = useDeliveryRouteBuilderInteraction({
    filterableSelectedOrderIds: selectedEditableOrderIds.filter(
      (orderId) => !routeStopIdSet.has(orderId)
    ),
    initialEnabledBuckets: getDeliveryRouteMapBucketsForOrders(routeDisplayStops),
    onSelectedOrderIdsChange: updateSelectedOrderIds,
    orders: availableListOrders,
    pinnedOrderIds: routeStopOrderIds,
    selectedOrderIds: selectedEditableOrderIds,
  })
  const visibleSelectedRouteOrderCount = filteredAvailableListOrders.filter(
    (order) =>
      lockedStopIdSet.has(getDeliveryRouteSelectionId(order)) ||
      selectedEditableOrderIds.includes(getDeliveryRouteSelectionId(order))
  ).length

  useEffect(() => {
    isRedirectingAfterUpdateRef.current = false
    editRouteForm.reset({
      name: share.name ?? '',
      sales_order_ids: initialEditableStopIds,
    })
    setManualEditStops(serverManualStops)
    setManualDialogState(null)
    setManualStopPendingRemoval(null)
    setRemovedManualStopIds(new Set())
    // The form object is stable enough for this local reset; the server route ids/name are the reset trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableStopFormKey])

  const selectedEditableOrders = availableOrders.filter((order) =>
    selectedEditableOrderIds.includes(getDeliveryRouteSelectionId(order))
  )
  const hasSelectedUnverifiedOrders = selectedEditableOrders.some((order) =>
    isDeliveryAddressExplicitlyUnverified(order.delivery_address)
  )

  const handleToggleEditableOrder = (orderId, checked) => {
    if (isReadOnly) {
      return
    }

    if (lockedStopIdSet.has(orderId) || (manualStopIdSet.has(orderId) && !checked)) {
      return
    }

    const nextIds = new Set(selectedEditableOrderIds)

    if (checked) {
      if (getDeliveryRouteSelectionCount(nextIds, lockedStopIds) >= DELIVERY_ROUTE_STOP_LIMIT) {
        toast.error(DELIVERY_ROUTE_STOP_LIMIT_MESSAGE)
        return
      }
      nextIds.add(orderId)
    } else {
      nextIds.delete(orderId)
    }

    updateSelectedOrderIds(Array.from(nextIds))
  }

  const handleToggleEditableOrders = (actions) => {
    if (isReadOnly) {
      return
    }

    const orderActions = actions.filter(
      ({ checked, orderId }) => checked || !manualStopIdSet.has(orderId)
    )
    const { selectedOrderIds, stopLimitReached } = applyDeliveryRouteSelectionActions({
      actions: orderActions,
      lockedOrderIds: lockedStopIds,
      selectedOrderIds: selectedEditableOrderIds,
    })

    if (stopLimitReached) {
      toast.error(DELIVERY_ROUTE_STOP_LIMIT_MESSAGE)
    }

    updateSelectedOrderIds(selectedOrderIds)
  }

  const handleSelectEditableOrders = (orderIds) => {
    if (isReadOnly) {
      return
    }

    const { selectedOrderIds, stopLimitReached } = applyDeliveryRouteSelectionActions({
      actions: orderIds.map((orderId) => ({ checked: true, orderId })),
      lockedOrderIds: lockedStopIds,
      selectedOrderIds: selectedEditableOrderIds,
    })

    if (stopLimitReached) {
      toast.error(DELIVERY_ROUTE_STOP_LIMIT_MESSAGE)
    }

    updateSelectedOrderIds(selectedOrderIds)
  }

  const saveManualStop = (manualStop) => {
    if (isReadOnly) {
      return false
    }

    const editingStop = manualDialogState?.stop
    const selectionId =
      editingStop?.selection_id ?? getNextManualDeliveryDraftSelectionId(manualEditStops)
    const isExistingDraft = Boolean(editingStop)

    if (
      !canSaveManualDeliveryDraft({
        currentStopCount: selectedRouteStopCount,
        isExistingDraft,
      })
    ) {
      toast.error(DELIVERY_ROUTE_STOP_LIMIT_MESSAGE)
      return false
    }

    const nextStop = buildManualDeliveryDisplayStop(
      {
        ...editingStop,
        ...manualStop,
        id: manualStop.id ?? editingStop?.route_stop_id ?? null,
      },
      share,
      selectionId
    )

    setManualEditStops((currentStops) => {
      const existingIndex = currentStops.findIndex(
        (stop) => getDeliveryRouteSelectionId(stop) === selectionId
      )

      if (existingIndex < 0) {
        return [...currentStops, nextStop]
      }

      return currentStops.map((stop, index) => (index === existingIndex ? nextStop : stop))
    })

    if (!isExistingDraft) {
      editRouteForm.setFieldValue('sales_order_ids', [...selectedEditableOrderIds, selectionId])
    }

    setManualDialogState(null)
    return true
  }

  const requestManualStopRemoval = (manualStop) => {
    if (isReadOnly) {
      return
    }

    if (selectedRouteStopCount <= 1) {
      toast.error('A delivery route needs at least one stop', {
        description: 'Add another delivery first, or delete the route instead.',
      })
      return
    }

    setManualStopPendingRemoval(manualStop)
  }

  const confirmManualStopRemoval = () => {
    if (isReadOnly) {
      return
    }

    const selectionId = getDeliveryRouteSelectionId(manualStopPendingRemoval)

    editRouteForm.setFieldValue(
      'sales_order_ids',
      selectedEditableOrderIds.filter((orderId) => orderId !== selectionId)
    )

    if (manualStopPendingRemoval?.route_stop_id) {
      setRemovedManualStopIds((current) => new Set(current).add(selectionId))
    } else {
      setManualEditStops((current) =>
        current.filter((stop) => getDeliveryRouteSelectionId(stop) !== selectionId)
      )
    }

    setManualStopPendingRemoval(null)
  }

  const resetRouteDraft = () => {
    editRouteForm.reset()
    setManualEditStops(serverManualStops)
    setManualDialogState(null)
    setManualStopPendingRemoval(null)
    setRemovedManualStopIds(new Set())
  }

  return (
    <div className="w-full space-y-4 pb-0 xl:grid xl:h-[calc(100dvh-5.5rem)] xl:min-h-0 xl:grid-rows-[auto_auto_minmax(0,1fr)] xl:gap-4 xl:space-y-0 xl:overflow-hidden">
      <div className="space-y-3 xl:shrink-0">
        <AdminPageHeader
          icon={RouteIcon}
          title="Edit Delivery Route"
          badges={isStartedRoute ? <Badge variant="info">Started</Badge> : null}
        />
      </div>

      <editRouteForm.AppForm>
        <editRouteForm.Form className="space-y-3 xl:shrink-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1 sm:max-w-xl">
              <editRouteForm.AppField name="name">
                {(field) => (
                  <field.TextInput
                    label="Route Name"
                    placeholder="Friday hospital deliveries"
                    characterMaxLength={120}
                    disabled={isReadOnly}
                  />
                )}
              </editRouteForm.AppField>
            </div>
          </div>

          <SaveBar
            forceDirty={hasManualStopDraftChanges}
            mutation={updateDeliveryShare}
            onReset={resetRouteDraft}
            shouldBlockNavigation={() => !isRedirectingAfterUpdateRef.current}
            unsavedChangesText="Unsaved route changes"
          />
        </editRouteForm.Form>
      </editRouteForm.AppForm>

      <div className="space-y-4 xl:min-h-0 xl:space-y-0 xl:overflow-hidden">
        <div className="grid gap-4 xl:h-full xl:min-h-0 xl:grid-cols-[minmax(18rem,24rem)_minmax(0,1fr)] xl:items-stretch">
          <div className="min-w-0 xl:order-2 xl:min-h-0">
            <DeliveryRouteBuilderMap
              orders={availableListOrders}
              anchorOrder={routeDisplayStops[0]}
              enabledBuckets={mapEnabledBuckets}
              highlightedOrderIds={highlightedCardOrderIds}
              isReadOnly={isReadOnly}
              pinnedOrderIds={routeStopOrderIds}
              selectedOrderIds={selectedMapOrderIds}
              onEnabledBucketsChange={handleMapEnabledBucketsChange}
              onHoveredOrderIdsChange={setHighlightedMapOrderIds}
              onOptimizedRouteSummaryChange={setOptimizedRouteSummary}
              onToggleOrder={handleToggleEditableOrder}
              onToggleOrders={handleToggleEditableOrders}
              onSelectOrders={handleSelectEditableOrders}
            />
          </div>

          <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border bg-background xl:order-1 xl:h-full xl:min-w-0">
            <div className="shrink-0 border-b bg-background p-4">
              <EditRouteSelectionHeader
                canAddManualDelivery={
                  !isReadOnly && selectedRouteStopCount < DELIVERY_ROUTE_STOP_LIMIT
                }
                onAddManualDelivery={() => setManualDialogState({ stop: null })}
                routeMetricSummary={routeMetricSummary}
                selectedOrderCount={visibleSelectedRouteOrderCount}
                visibleOrderCount={filteredAvailableListOrders.length}
              />
            </div>

            {hasSelectedUnverifiedOrders ? (
              <div className="shrink-0 border-b border-status-destructive-border bg-status-destructive-background p-4">
                <Typography variant="label" tone="destructive" className="block">
                  Fix or remove the selected unverified stop before you save this route.
                </Typography>
              </div>
            ) : null}

            <ScrollArea
              className="h-[28rem] min-h-0 overflow-hidden xl:h-0 xl:flex-1 xl:min-h-0"
              viewportClassName="rounded-none"
            >
              <div className="p-4 pr-3">
                {filteredAvailableListOrders.length > 0 ? (
                  <div className="space-y-3">
                    {filteredAvailableListOrders.map((order) => {
                      const selectionId = getDeliveryRouteSelectionId(order)
                      const isLockedStop = lockedStopIdSet.has(selectionId)

                      return (
                        <SelectableDeliveryRouteOrderRow
                          key={selectionId}
                          anchorOrder={routeDisplayStops[0]}
                          order={order}
                          isSelected={
                            isLockedStop || selectedEditableOrderIds.includes(selectionId)
                          }
                          isLocked={isLockedStop}
                          isMapHighlighted={highlightedMapOrderIdSet.has(selectionId)}
                          isReadOnly={isReadOnly}
                          onHoverChange={handleCardHoverChange}
                          scrollIntoViewOnMapHighlight={scrollToHighlightedOrderId === selectionId}
                          routeStopNumber={
                            routeStopIdSet.has(selectionId)
                              ? routeStopOrderIds.indexOf(selectionId) + 1
                              : null
                          }
                          isSelectableOverride={
                            !isLockedStop &&
                            (editableStopIdSet.has(selectionId) || isDeliveryRouteEligible(order))
                          }
                          onToggle={handleToggleEditableOrder}
                          onEditManualDelivery={
                            !isReadOnly && order.source_type === 'manual'
                              ? (manualStop) => setManualDialogState({ stop: manualStop })
                              : undefined
                          }
                          onRemoveManualDelivery={
                            !isReadOnly && order.source_type === 'manual'
                              ? requestManualStopRemoval
                              : undefined
                          }
                          blockedDescription="Remove this stop or fix the delivery address before saving."
                        />
                      )
                    })}
                  </div>
                ) : (
                  <EmptyState
                    size="inline"
                    title={
                      availableListOrders.length > 0
                        ? 'No deliveries match the selected map filters.'
                        : 'No editable deliveries are available for this route.'
                    }
                  />
                )}
              </div>
            </ScrollArea>
          </section>
        </div>

        <DeliveryRouteHiddenSelectionDialog
          pendingFilterChange={pendingMapFilterChange}
          onDismiss={() => setPendingMapFilterChange(null)}
          onConfirm={confirmPendingMapFilterChange}
        />
      </div>

      {manualDialogState ? (
        <ManualDeliveryDialog
          key={manualDialogState.stop?.selection_id ?? 'new-manual-delivery'}
          open
          initialStop={
            manualDialogState.stop ? buildDeliveryRouteStopPayload(manualDialogState.stop) : null
          }
          defaultDeliveryDate={share.delivery_date}
          defaultTimezone={share.location?.address?.timezone || share.location?.timezone}
          onOpenChange={(open) => {
            if (!open) setManualDialogState(null)
          }}
          onSave={saveManualStop}
        />
      ) : null}

      <ConfirmationDialog
        open={Boolean(manualStopPendingRemoval)}
        onOpenChange={(open) => {
          if (!open) setManualStopPendingRemoval(null)
        }}
        trigger={null}
        variant="destructive"
        title={`Remove ${manualStopPendingRemoval?.recipient_name || 'custom stop'}?`}
        description={
          manualStopPendingRemoval?.route_stop_id
            ? 'This custom stop exists only on this delivery route. After you save the route, it will be deleted and will not be available to add to another route.'
            : 'This custom stop has not been saved yet. You’ll need to enter it again if you change your mind.'
        }
        confirmText="Remove stop"
        onConfirm={confirmManualStopRemoval}
      />
    </div>
  )
}
